import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@futuredev/shared-auth';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string | null;
}

function AdminProtectedRoute({ children, requiredRole = null }: AdminProtectedRouteProps) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user is admin, super user, or staff (employee) - match Dashboard.jsx logic exactly
  const rawRole = user.role || user.user_role || user.userRole;
  const userRole = typeof rawRole === 'string' ? rawRole : '';
  const normalizedRole = userRole.toUpperCase().trim();

  // If requiredRole is specified, check that role
  if (requiredRole) {
    const requiredRoleUpper = requiredRole.toUpperCase().trim();

    // Handle special case: ADMIN_OR_STAFF means admin, super user, or employee
    if (requiredRoleUpper === 'ADMIN_OR_STAFF') {
      const isAdminOrStaff = normalizedRole === 'ROOT' ||
        normalizedRole === 'ADMINISTRATOR_USER' ||
        normalizedRole === 'SUPER_USER' ||
        normalizedRole === 'EMPLOYEE_USER';

      if (!isAdminOrStaff) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
              <p className="text-gray-600 mb-4">This page requires ADMIN_OR_STAFF privileges.</p>
              <p className="text-sm text-gray-500 mb-4">
                Your role: {normalizedRole || userRole || 'Unknown'}
              </p>
              <a href="/dashboard" className="text-primary-600 hover:text-primary-700">
                Return to Dashboard
              </a>
            </div>
          </div>
        );
      }

      return children;
    }

    // For other specific roles, do exact match
    const hasRequiredRole = normalizedRole === requiredRoleUpper;

    if (!hasRequiredRole) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
            <p className="text-gray-600 mb-4">This page requires {requiredRole} privileges.</p>
            <p className="text-sm text-gray-500 mb-4">
              Your role: {normalizedRole || userRole || 'Unknown'}
            </p>
            <a href="/dashboard" className="text-primary-600 hover:text-primary-700">
              Return to Dashboard
            </a>
          </div>
        </div>
      );
    }

    return children;
  }

  // Default behavior: Allow admin, super user, and staff (employee)
  const isAdmin = normalizedRole === 'ROOT' || normalizedRole === 'ADMINISTRATOR_USER' || normalizedRole === 'SUPER_USER' || normalizedRole === 'EMPLOYEE_USER';

  // Production safety: never grant access by email.
  // If you need a dev-only bypass, do it explicitly behind a dev flag in the backend, not here.
  const isAllowed = isAdmin;

  if (!isAllowed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-4">You do not have permission to access this page.</p>
          <p className="text-sm text-gray-500 mb-4">
            Your role: {normalizedRole || userRole || 'Unknown'}
          </p>
          <a href="/dashboard" className="text-primary-600 hover:text-primary-700">
            Return to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return children;
}

export default AdminProtectedRoute;

